import { useQuery } from '@tanstack/react-query';

import { companyApi } from '@/api';
import {
  Card,
  CardHeader,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
  PageHeader,
  ProgressBar,
} from '@/components/ui';
import { RecruitmentPerformance } from '@/features/analytics/RecruitmentPerformance';
import { RecruitmentSummary } from '@/features/analytics/RecruitmentSummary';
import { count } from '@/features/analytics/format';
import { DashboardInternships } from '@/features/internships/DashboardInternships';
import { errorMessage } from '@/hooks/useApiError';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';

// const FIGURES = [
//   { key: 'total', label: 'Total Internships', icon: 'briefcase', className: 'text-brand-600' },
//   { key: 'open', label: 'Open Internships', icon: 'check', className: 'text-success-600' },
//   { key: 'filled', label: 'Filled Internships', icon: 'shieldCheck', className: 'text-brand-600' },
//   { key: 'closed', label: 'Closed Internships', icon: 'close', className: 'text-danger-600' },
//   { key: 'applications', label: 'Total Applications', icon: 'users', className: 'text-brand-600' },
// ];
const FIGURES = [
  {
    key: 'total',
    label: 'Total Internships',
    icon: 'briefcase',
    tint: 'bg-brand-50 text-brand-700',
    edge: 'bg-brand-500',
  },
  {
    key: 'open',
    label: 'Open Internships',
    icon: 'check',
    tint: 'bg-success-50 text-success-700',
    edge: 'bg-success-500',
  },
  {
    key: 'filled',
    label: 'Filled Internships',
    icon: 'shieldCheck',
    tint: 'bg-info-50 text-info-700',
    edge: 'bg-info-500',
  },
  {
    key: 'closed',
    label: 'Closed Internships',
    icon: 'close',
    tint: 'bg-danger-50 text-danger-700',
    edge: 'bg-danger-500',
  },
  {
    key: 'applications',
    label: 'Total Applications',
    icon: 'users',
    tint: 'bg-warning-50 text-warning-800',
    edge: 'bg-warning-500',
  },
];

/**
 * The company's front page: how many internships it has, the latest of them,
 * the recruitment analytics and what is left of its profile. Each block shows
 * only to members who may read it.
 */
export function CompanyDashboardPage() {
  const { can } = usePermissions();
  const canReadInternships = can(PERMISSIONS.INTERNSHIP_READ);
  const canEditProfile = can(PERMISSIONS.COMPANY_PROFILE_UPDATE);

  const {
    data: company,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['company-profile'],
    queryFn: companyApi.getProfile,
  });

  // Under the counts' key, so whatever changes those refreshes these too.
  const { data: summary } = useQuery({
    queryKey: ['company-internship-counts', 'summary'],
    queryFn: companyApi.getInternshipSummary,
    enabled: canReadInternships,
  });

  const { data: completion } = useQuery({
    queryKey: ['company-completion'],
    queryFn: companyApi.getCompletion,
    enabled: canEditProfile,
  });

  if (isPending) return <LoadingState label="Loading your workspace…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const percentage = completion?.percentage ?? company.profile_completion;
  const missing = (completion?.sections ?? []).filter((section) => !section.complete);

  return (
    <div>
      <PageHeader title="Company Dashboard" />

      {canReadInternships && (
        <>
          <section
            aria-label="Internship figures"
            className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5"
          >
            {FIGURES.map((figure) => (
              // <Card key={figure.key} className="text-center">
              //   <Icon name={figure.icon} size={24} className={`${figure.className} mx-auto`} />
              //   <p className="text-ink mt-3 text-2xl font-semibold tabular-nums">
              //     {summary ? count(summary[figure.key]) : '…'}
              //   </p>
              //   <p className="text-ink-muted mt-1 text-sm">{figure.label}</p>
              // </Card>
              <Card
                key={figure.key}
                className="relative overflow-hidden text-center transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <span
                  aria-hidden="true"
                  className={`${figure.edge} absolute inset-x-0 top-0 h-1`}
                />
                <span
                  aria-hidden="true"
                  className={`${figure.tint} mx-auto mt-1 grid size-11 place-items-center rounded-full`}
                >
                  <Icon name={figure.icon} size={20} />
                </span>
                <p className="text-ink mt-3 text-3xl font-bold tabular-nums">
                  {summary ? count(summary[figure.key]) : '…'}
                </p>
                <p className="text-ink-muted mt-1 text-sm">{figure.label}</p>
              </Card>
            ))}
          </section>

          <DashboardInternships companyName={company.name} />
        </>
      )}

      {can(PERMISSIONS.ANALYTICS_READ) && (
        <section aria-labelledby="recruitment-analytics" className="mt-8">
          <h2 id="recruitment-analytics" className="text-ink text-xl font-semibold tracking-tight">
            Analytics
          </h2>
          <RecruitmentSummary />
          <RecruitmentPerformance />
        </section>
      )}

      {canEditProfile && percentage < 100 && (
        <Card className="mt-6">
          <CardHeader
            title="Finish your profile"
            description="A complete profile is required before verification."
            action={
              <LinkButton to={paths.company.profile} variant="secondary" size="sm">
                Edit profile
              </LinkButton>
            }
          />
          <ProgressBar value={percentage} label="Complete" />
          <ul className="mt-4 space-y-2">
            {missing.map((section) => (
              <li key={section.key} className="flex items-start gap-2.5 text-sm">
                <span
                  className="mt-1.5 size-1.5 shrink-0 bg-warning-500/80 rounded-full"
                  aria-hidden="true"
                />
                <span className="min-w-0">
                  <span className="text-ink font-medium">{section.label}</span>
                  <span className="text-ink-muted block text-xs">{section.hint}</span>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
